import clsx from 'clsx';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import * as Yup from 'yup';

import { useAuthContext } from '@/auth/useAuthContext';
import { KeenIcon } from '@/components';
import { useLayout } from '@/providers';
import { useSnackbar } from 'notistack';

const initialValues = {
  email: ''
};

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Ungültiges E-Mail-Format')
    .min(3, 'Mindestens 3 Zeichen')
    .max(50, 'Maximal 50 Zeichen')
    .required('E-Mail ist erforderlich')
});

const ResetPassword = () => {
  const [loading, setLoading] = useState(false);
  const { requestPassword } = useAuthContext();
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (!requestPassword) {
          throw new Error('JWTProvider ist für dieses Formular erforderlich.');
        }

        // 🔐 Wichtig:
        // Backend gibt IMMER 200 zurück – egal ob User existiert oder nicht
        await requestPassword(values.email);

        enqueueSnackbar(
          'Wenn die E-Mail-Adresse existiert, wurde eine Nachricht zum Zurücksetzen des Passworts versendet.',
          { variant: 'success' }
        );

        navigate('/auth/reset-password/check-email');
      } catch {
        enqueueSnackbar('Beim Zurücksetzen des Passworts ist ein Fehler aufgetreten.', {
          variant: 'error'
        });
      } finally {
        setLoading(false);
      }
    }
  });

  return (
    <div className="card max-w-[370px] w-full">
      <form
        className="card-body flex flex-col gap-5 p-10"
        noValidate
        onSubmit={formik.handleSubmit}
      >
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">Passwort zurücksetzen</h3>
          <span className="text-2sm text-gray-600 font-medium">
            Geben Sie Ihre E-Mail-Adresse ein, um Ihr Passwort zurückzusetzen.
          </span>
        </div>

        <div className="flex flex-col gap-1">
          <label className="form-label text-gray-900">E-Mail</label>
          <label className="input">
            <input
              type="email"
              placeholder="E-Mail-Adresse eingeben"
              autoComplete="off"
              {...formik.getFieldProps('email')}
              className={clsx(
                'form-control bg-transparent',
                { 'is-invalid': formik.touched.email && formik.errors.email },
                { 'is-valid': formik.touched.email && !formik.errors.email }
              )}
            />
          </label>
          {formik.touched.email && formik.errors.email && (
            <span role="alert" className="text-danger text-xs mt-1">
              {formik.errors.email}
            </span>
          )}
        </div>

        <div className="flex flex-col gap-5 items-stretch">
          <button
            type="submit"
            className="btn btn-primary flex justify-center grow"
            disabled={loading}
          >
            {loading ? 'Bitte warten …' : 'Weiter'}
          </button>

          <Link
            to={currentLayout?.name === 'auth-branded' ? '/auth/login' : '/auth/classic/login'}
            className="flex items-center justify-center text-sm gap-2 text-gray-700 hover:text-primary"
          >
            <KeenIcon icon="black-left" />
            Zurück zum Login
          </Link>
        </div>
      </form>
    </div>
  );
};

export { ResetPassword };
